import { Module } from 'vuex';
import { RootState } from '@/app/app.store';
import { User } from '@/user/show/user-show.store';
import { apiHttpClient } from '@/app/app.service';

export interface Series {
  id: string;
  title: string;
  user: User;
  duration: string;
  nodeCount: string;
  packCount: number;
  packs: Array<PackItem>;
  progress: {
    percent: number;
    completed: number;
  };
}

export interface NodeItem {
  id: string;
  title: string;
  duration: string;
  completed: string;
}

export interface PackItem {
  packId: string;
  packtitle: string;
  packIndex: number;
  nodes: Array<NodeItem>;
  duration: string;
  nodeCount: string;
  progress: string;
}

export interface SeriesShowStoreState {
  seriesCache: Array<Series>;
  series: Series | null;
  loading: boolean;
  name: string;
}

export const seriesShowStoreModule: Module<SeriesShowStoreState, RootState> = {
  /**
   * 命名空间
   */
  namespaced: true,

  /**
   * 数据
   */
  state: {
    seriesCache: [],
    series: null,
    loading: false,
    name: 'seriesShowStoreModule',
  } as SeriesShowStoreState,

  /**
   * 获取器
   */
  getters: {
    seriesCache(state) {
      return state.seriesCache;
    },

    series(state) {
      return state.series;
    },

    loading(state) {
      return state.loading;
    },
  },

  /**
   * 修改器
   */
  mutations: {
    setSeriesCache(state, data) {
      // state.seriesCache = data;
      const seriesCache = state.seriesCache.find(
        series => series.id === data.id,
      );

      if (!seriesCache) {
        state.seriesCache = [...state.seriesCache, data];
      }
    },

    setSeries(state, data) {
      state.series = data;
    },

    setLoading(state, data) {
      state.loading = data;
    },

    setCompleted(state, data) {
      const { nodeId, completed } = data;

      if (
        state.series &&
        !state.series.progress &&
        parseInt(`${completed}`, 10)
      ) {
        state.series.progress = {
          percent: 0,
          completed: 0,
        };
      }

      // 增加完成数量
      if (state.series && parseInt(`${completed}`, 10)) {
        state.series.progress.completed++;
      }

      // 减少完成数量
      if (state.series && !parseInt(`${completed}`, 10)) {
        state.series.progress.completed--;
      }

      // 更新完成百分比
      if (state.series) {
        state.series.progress.percent = parseFloat(
          `${(
            (state.series.progress.completed /
              parseInt(`${state.series.nodeCount}`, 10)) *
            100
          ).toFixed(2)}`,
        );
      }

      // 更新节点完成情况
      if (state.series) {
        const packs = state.series.packs.map(pack => {
          pack.nodes = pack.nodes.map(node => {
            if (node.id === nodeId) {
              node.completed = completed;
            }

            return node;
          });

          const completedNodes = pack.nodes.filter(
            node => parseInt(`${node.completed}`, 10) === 1,
          ).length;

          pack.progress = (
            (completedNodes / parseInt(`${pack.nodeCount}`, 10)) *
            100
          ).toFixed(2);

          return pack;
        });

        state.series = {
          ...state.series,
          packs,
        };
      }
    },
  },

  /**
   * 动作
   */
  actions: {
    async getSeriesById({ commit }, seriesId) {
      commit('setLoading', true);

      try {
        const response = await apiHttpClient.get(`/series/${seriesId}`);
        commit('setLoading', false);
        commit('setSeries', response.data);
        commit('setSeriesCache', response.data);

        return response;
      } catch (error) {
        commit('setLoading', false);

        throw error.response;
      }
    },

    async getSeriesCache({ state, dispatch, commit }, seriesId) {
      let series = state.seriesCache.find(series => series.id === seriesId);

      if (!series) {
        const response = await dispatch('getSeriesById', seriesId);
        series = response.data;
      }

      commit('setSeries', series);

      return series;
    },
  },

  /**
   * 模块
   */
  modules: {},
};
